import { getWebBaseURLSync } from '../getWebBaseURL';
// ⚠️ leaf 모듈에서 직접 가져온다. `convertDeeplinkToWebLink` 를 거치면 resolveWebURL →
// LocalStorageBridge → react-native 까지 끌려온다(sync·순수여야 하는 이 모듈에 불필요).
import { isNativeOnlyWebviewPath } from '../convertDeeplinkToWebLink/nativeOnlyWebviewPath';

/**
 * `ably://webview?...` 딥링크에서 `url` 값만 **원문 그대로** 뽑는다.
 *
 * `URLSearchParams` 를 쓰지 않는 이유: url 값이 인코딩되지 않은 채 자체 쿼리를 갖는 형태
 * (`url=https://m.a-bly.com/x?a=1&b=2`)에서 `&b=2` 가 딥링크의 다른 파라미터로 파싱되어
 * 잘려나간다.
 *
 * 대신 `url=` 뒤 전체를 잡고, **인코딩되지 않은 `?` 가 없으면** 첫 `&` 부터 잘라낸다.
 * 그 `&` 는 안쪽 쿼리가 아니라 딥링크 자신의 다음 파라미터이기 때문이다.
 *
 * - `url=https://m.a-bly.com/x?a=1&b=2` → `https://m.a-bly.com/x?a=1&b=2` (안쪽 `?` 있음)
 * - `url=events%2F456&title_bar=true`   → `events%2F456` (안쪽 `?` 없음 → `&` 에서 절단)
 */
export const extractWebviewUrlParam = (deeplink: string): string | null => {
  const match = deeplink.match(/[?&]url=(.*)$/);
  if (match === null) return null;

  const raw = match[1];
  if (raw.length === 0) return null;

  const ampersandIndex = raw.indexOf('&');
  const questionIndex = raw.indexOf('?');
  const hasInnerQuery = questionIndex !== -1 && questionIndex < ampersandIndex;
  const value =
    ampersandIndex !== -1 && !hasInnerQuery
      ? raw.slice(0, ampersandIndex)
      : raw;

  // 인코딩된 값이면 되돌린다. 잘못된 인코딩(`%` 단독 등)은 원문 유지.
  if (!value.includes('%')) return value;
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

/**
 * `ably://webview?...` 딥링크를 **웹에서 열 절대 URL** 로 만든다(아니면 null).
 *
 * url 값이 이미 절대 URL 이면 그대로 쓰고, path 만 있으면 환경 origin 을 붙인다.
 * origin 은 `getWebBaseURLSync` 가 호스트명으로 판별한다 —
 * prod(`https://m.a-bly.com`) / 스테이징(`https://m-stag-N.a-bly.com`) / 로컬(`:3000`).
 *
 * 웹 미지원(네이티브 전용) 경로는 null 을 반환해, 호출부가 기존 흐름(앱 다운로드 유도)으로
 * 흘러가게 한다.
 *
 * sync 로 둔 이유: 새 탭(`window.open`)을 여는 호출부가 await 를 거치면 user activation 이
 * 끊겨 팝업 차단에 걸릴 수 있다.
 */
export const resolveWebviewDeeplinkUrl = (deeplink: string): string | null => {
  const urlParam = extractWebviewUrlParam(deeplink);
  if (urlParam === null) return null;

  const path = isAbsoluteUrl(urlParam)
    ? getPathname(urlParam)
    : withLeadingSlash(urlParam);

  if (path !== null && isNativeOnlyWebviewPath(path)) return null;

  return toAbsoluteWebUrl(urlParam);
};

/**
 * path 면 환경 origin(prod / 스테이징 / 로컬 :3000)을 붙여 절대 URL 로 만든다.
 * 이미 절대 URL 이면 그대로 반환.
 */
export const toAbsoluteWebUrl = (urlOrPath: string): string => {
  if (isAbsoluteUrl(urlOrPath)) return urlOrPath;
  return `${getWebBaseURLSync()}${withLeadingSlash(urlOrPath)}`;
};

const isAbsoluteUrl = (value: string): boolean => /^https?:\/\//.test(value);

const withLeadingSlash = (path: string): string =>
  path.startsWith('/') ? path : `/${path}`;

const getPathname = (url: string): string | null => {
  try {
    return new URL(url).pathname;
  } catch {
    return null;
  }
};
